import React, { useLayoutEffect, useRef, useState } from 'react';
import { withImageShade } from '../model/imageShade';
import {
  computeCropBackgroundStyle,
  shouldShowCropBlurFill,
  toCssUrl,
} from '../utils/imageCropMath';

type Crop = {
  zoom?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
  zoomLibre?: boolean;
};

type CropSurfaceProps = React.HTMLAttributes<HTMLDivElement> & {
  imageUrl?: string;
  mobileImageUrl?: string;
  crop?: Crop;
  frameRatio?: number;
  imageShade?: unknown;
};

function shadeStyle(style: React.CSSProperties | undefined, imageShade: unknown) {
  if (!style?.backgroundImage || typeof style.backgroundImage !== 'string') return style;
  return { ...style, backgroundImage: withImageShade(style.backgroundImage, imageShade) };
}

function useCropBackgroundStyle(
  imageUrl: string | undefined,
  crop: Crop | undefined,
  frameRatio: number | undefined,
  imageShade: unknown,
) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(
    imageUrl
      ? shadeStyle(
          {
            backgroundImage: toCssUrl(imageUrl),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          },
          imageShade,
        )
      : undefined,
  );

  const showBlur = shouldShowCropBlurFill({ zoomLibre: crop?.zoomLibre, zoom: crop?.zoom });

  useLayoutEffect(() => {
    if (!imageUrl) {
      setStyle(undefined);
      return undefined;
    }

    let cancelled = false;
    const img = new Image();

    const apply = () => {
      const el = surfaceRef.current;
      if (cancelled || !el || !img.naturalWidth) return;
      setStyle(
        shadeStyle(
          computeCropBackgroundStyle({
            imageUrl,
            containerWidth: el.clientWidth,
            containerHeight: el.clientHeight,
            imageWidth: img.naturalWidth,
            imageHeight: img.naturalHeight,
            zoom: crop?.zoom,
            offsetX: crop?.offsetX,
            offsetY: crop?.offsetY,
            frameRatio,
          }) as React.CSSProperties,
          imageShade,
        ),
      );
    };

    img.onload = apply;
    img.src = imageUrl;
    if (img.complete && img.naturalWidth) apply();

    const el = surfaceRef.current;
    const observer = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(apply) : null;
    if (observer && el) observer.observe(el);
    window.addEventListener('resize', apply);

    return () => {
      cancelled = true;
      img.onload = null;
      if (observer) observer.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [imageUrl, crop?.zoom, crop?.offsetX, crop?.offsetY, crop?.zoomLibre, frameRatio, imageShade]);

  return { surfaceRef, style, showBlur };
}

export default function CropSurface({
  className,
  imageUrl,
  mobileImageUrl,
  crop,
  frameRatio,
  imageShade,
  children,
  ...rest
}: CropSurfaceProps) {
  const { surfaceRef, style, showBlur } = useCropBackgroundStyle(
    imageUrl,
    crop,
    frameRatio,
    imageShade,
  );
  const hasMobileImage = Boolean(mobileImageUrl);
  const classes = [
    className,
    'preview-crop-surface',
    imageUrl ? 'preview-crop-surface--has-image' : '',
    hasMobileImage ? 'preview-crop-surface--has-mobile' : '',
    showBlur ? 'preview-crop-surface--blur' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const surfaceStyle =
    showBlur && imageUrl
      ? ({
          ['--crop-blur-image' as string]: toCssUrl(imageUrl),
        } as React.CSSProperties)
      : undefined;

  return (
    <div className={classes} ref={surfaceRef} style={surfaceStyle} {...rest}>
      {imageUrl ? (
        <div aria-hidden='true' className='preview-crop-surface__clip'>
          <div
            className='preview-crop-surface__media preview-crop-surface__media--desktop'
            style={style}
          />
          {hasMobileImage ? (
            <div
              className='preview-crop-surface__media preview-crop-surface__media--mobile'
              style={{
                backgroundImage: withImageShade(toCssUrl(mobileImageUrl), imageShade),
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
